import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle, AlertCircle, Loader2,
  Home, Users, Heart, CreditCard, FileText
} from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { StatusCard } from '~/components/StatusCard';
import { WizardFormLayout } from '~/components/forms/WizardFormLayout';
import { useMultiStepForm, type StepDefinition } from '~/hooks/useMultiStepForm';
import {
  AppFamilyInfoStep,
  AppStudentsStep,
  AppStudentMedicalStep,
  AppTuitionStep,
  AppReviewStep,
} from './admissions-steps';
import { useOpenAdmissionsForm } from './useOpenAdmissionsForm';
import { useSaveAdmissionsFormDraft } from './useSaveAdmissionsFormDraft';
import { useSubmitAdmissionsForm } from './useSubmitAdmissionsForm';
import LinkReplaced from './LinkReplaced';
import { linkReplacedSchoolName } from './linkReplacedError';

type AdmissionsStepId = 'family' | 'students' | 'medical' | 'tuition' | 'review';

const STEPS: StepDefinition<AdmissionsStepId>[] = [
  { id: 'family', label: 'Family Info', icon: Home },
  { id: 'students', label: 'Students', icon: Users },
  { id: 'medical', label: 'Medical', icon: Heart },
  { id: 'tuition', label: 'Tuition', icon: CreditCard },
  { id: 'review', label: 'Review', icon: FileText },
];

function AdmissionsForm() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [replacedBy, setReplacedBy] = useState<string | null>(null);
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Track tab review states
  const [familyInfoReviewed, setFamilyInfoReviewed] = useState(false);
  const [medicalReviewed, setMedicalReviewed] = useState(false);

  const [getForm] = useOpenAdmissionsForm();
  const [saveDraft] = useSaveAdmissionsFormDraft();
  const [submitForm, { loading: submitting }] = useSubmitAdmissionsForm();

  const loadForm = useCallback(async () => {
    let result;
    try {
      result = await getForm({ variables: { token: token! } });
    } catch (err) {
      const schoolName = linkReplacedSchoolName(err);
      if (schoolName) setReplacedBy(schoolName);
      throw err;
    }
    const formResult = result.data!.openAdmissionsForm!;
    const answers = formResult.formAnswers as any;
    const userErrors = formResult.userErrors;

    if (userErrors && userErrors.length > 0) {
      throw new Error(userErrors[0].message);
    }

    // Restore reviewed states from saved answers
    if (answers?.reviewedSteps?.familyInfo) {
      setFamilyInfoReviewed(true);
    }
    if (answers?.reviewedSteps?.medical) {
      setMedicalReviewed(true);
    }

    // Build metadata entirely from live GraphQL fields (no longer reading from frozen metadata)
    const enhancedMetadata = {
      schoolYear: { name: formResult.schoolYearName },
      school: { name: formResult.schoolName },
      family: { familyName: formResult.familyName },
      availableOptions: {
        tuitionOptions: formResult.tuitionOptions || [],
        paymentOptions: formResult.paymentOptions || [],
        enrollmentFee: formResult.enrollmentFee || {},
        gradeLevels: formResult.gradeLevels || [],
      },
    };

    return { metadata: enhancedMetadata, answers };
  }, [token, getForm]);

  const saveDraftFn = useCallback(async (answers: any, stepIndex: number) => {
    await saveDraft({
      variables: {
        token: token!,
        formData: { ...answers, currentStep: stepIndex },
      }
    });
  }, [token, saveDraft]);

  const validateStep = useCallback((stepId: AdmissionsStepId, answers: any, metadata: any) => {
    const tuitionOptions = metadata?.availableOptions?.tuitionOptions || [];
    const paymentOptions = metadata?.availableOptions?.paymentOptions || [];

    switch (stepId) {
      case 'family': {
        if (!familyInfoReviewed) return false;
        // Primary contact must have an email
        const guardians = answers?.parentGuardians || [];
        const primary = guardians.find((g: any) => g.isPrimary);
        return !!primary?.email?.trim();
      }
      case 'students': {
        // NOTE: gradeLevel requires the school to have grade levels configured.
        // If none are configured, the dropdown will be empty and this step will block.
        const persisted = (answers?.students || []).filter((s: any) => s.firstName && s.lastName);
        const newOnes = (answers?.newStudents || []).filter((s: any) => s.firstName && s.lastName);
        const allStudents = [...persisted, ...newOnes];
        return allStudents.length > 0 && allStudents.every((s: any) => s.firstName && s.lastName && s.dateOfBirth && s.gradeLevel && s.gender);
      }
      case 'medical':
        return medicalReviewed && !!answers?.medicalConsentSignature?.trim();
      case 'tuition': {
        if (tuitionOptions.length === 0 || paymentOptions.length === 0) return true;
        const tPersisted = (answers?.students || []).filter((s: any) => s.firstName || s.lastName);
        const tNew = (answers?.newStudents || []).filter((s: any) => s.firstName || s.lastName);
        const tAll = [...tPersisted, ...tNew];
        const studentIds = tAll.map((s: any) => s.studentEnrollmentId || s.tempId);
        if (studentIds.length === 0) return true;
        const studentSelections = answers?.tuitionPreferences?.studentSelections || {};
        const familyPaymentOptionKey = answers?.tuitionPreferences?.paymentOptionKey;
        const allStudentsHaveTuition = studentIds.every((id: string) => studentSelections[id]?.tuitionOptionKey);
        return allStudentsHaveTuition && !!familyPaymentOptionKey;
      }
      case 'review': {
        const hasAcknowledgments = !!answers?.acknowledgments?.infoAccurate;
        const hasMedicalConsentSignature = !!answers?.medicalConsentSignature?.trim();
        return hasAcknowledgments && hasMedicalConsentSignature;
      }
      default:
        return true;
    }
  }, [familyInfoReviewed, medicalReviewed]);

  // Returns field-level validation errors for the current step.
  // Extensible: add new cases/fields as validation requirements grow.
  const getStepErrors = useCallback((stepId: AdmissionsStepId, answers: any, _metadata: any): { field: string; message: string }[] => {
    const errors: { field: string; message: string }[] = [];

    switch (stepId) {
      case 'family': {
        if (!familyInfoReviewed) {
          errors.push({ field: 'tabs', message: 'Please review each tab before continuing' });
        }
        const guardians = answers?.parentGuardians || [];
        const primary = guardians.find((g: any) => g.isPrimary);
        if (!primary) {
          errors.push({ field: 'guardians.primary', message: 'A primary contact must be designated' });
        } else if (!primary.email?.trim()) {
          errors.push({ field: 'guardians.primaryEmail', message: 'Primary contact must have an email address' });
        }
        break;
      }
      case 'students': {
        const persisted = (answers?.students || []).filter((s: any) => s.firstName && s.lastName);
        const newOnes = (answers?.newStudents || []).filter((s: any) => s.firstName && s.lastName);
        const allStudents = [...persisted, ...newOnes];
        if (allStudents.length === 0) {
          errors.push({ field: 'students', message: 'At least one student is required' });
        } else {
          allStudents.forEach((s: any, i: number) => {
            if (!s.dateOfBirth) errors.push({ field: `student.${i}.dateOfBirth`, message: `${s.firstName || 'Student'} is missing date of birth` });
            if (!s.gender) errors.push({ field: `student.${i}.gender`, message: `${s.firstName || 'Student'} is missing gender` });
            if (!s.gradeLevel) errors.push({ field: `student.${i}.gradeLevel`, message: `${s.firstName || 'Student'} is missing grade level` });
          });
        }
        break;
      }
      case 'medical': {
        if (!medicalReviewed) {
          errors.push({ field: 'tabs', message: 'Please review each student\'s medical information' });
        }
        if (!answers?.medicalConsentSignature?.trim()) {
          errors.push({ field: 'medicalConsentSignature', message: 'Medical consent signature is required' });
        }
        break;
      }
      default:
        break;
    }

    return errors;
  }, [familyInfoReviewed, medicalReviewed]);

  const form = useMultiStepForm<AdmissionsStepId>({
    steps: STEPS,
    loadForm,
    saveDraft: saveDraftFn,
    validateStep,
  });

  // Reset validation state when step changes
  useEffect(() => {
    setValidationAttempted(false);
  }, [form.currentStep]);

  // Compute current validation errors (only shown after user attempts Next)
  const validationErrors = useMemo(() => {
    if (!validationAttempted) return [];
    return getStepErrors(form.currentStepId, form.answers, form.metadata);
  }, [validationAttempted, getStepErrors, form.currentStepId, form.answers, form.metadata]);

  // Intercept Next: show errors instead of advancing when invalid
  const handleNext = useCallback(async () => {
    const errors = getStepErrors(form.currentStepId, form.answers, form.metadata);
    if (errors.length > 0) {
      setValidationAttempted(true);
      return;
    }
    setValidationAttempted(false);
    if (form.isLastStep) {
      setShowSubmitDialog(true);
    } else {
      await form.nextStep();
    }
  }, [getStepErrors, form.currentStepId, form.answers, form.metadata, form.isLastStep, form.nextStep]);

  // Sync reviewed states to formAnswers for persistence
  const handleFamilyInfoReviewed = useCallback((reviewed: boolean) => {
    setFamilyInfoReviewed(reviewed);
    if (reviewed && form.answers && !form.answers.reviewedSteps?.familyInfo) {
      form.setAnswers((prev: any) => ({
        ...prev,
        reviewedSteps: { ...prev?.reviewedSteps, familyInfo: true }
      }));
    }
  }, [form.answers, form.setAnswers]);

  const handleMedicalReviewed = useCallback((reviewed: boolean) => {
    setMedicalReviewed(reviewed);
    if (reviewed && form.answers && !form.answers.reviewedSteps?.medical) {
      form.setAnswers((prev: any) => ({
        ...prev,
        reviewedSteps: { ...prev?.reviewedSteps, medical: true }
      }));
    }
  }, [form.answers, form.setAnswers]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    try {
      const result = await submitForm({ variables: { token: token!, formData: form.answers } });
      const { userErrors } = result.data!.submitAdmissionsForm!;
      if (userErrors && userErrors.length > 0) {
        form.setError(userErrors[0].message);
        return;
      }
      setSubmitted(true);
      setShowSubmitDialog(false);
    } catch (err: any) {
      form.setError(err.message);
    }
  }, [token, form.answers, form.setError, submitForm]);

  // Derived
  const schoolYearName = form.metadata?.schoolYear?.name || 'Upcoming Year';
  const gradeLevels = form.metadata?.availableOptions?.gradeLevels || [];

  // Combine metadata and answers for step components
  const formData = useMemo(() => {
    if (!form.metadata || !form.answers) return null;
    return {
      students: form.answers.students || [],
      newStudents: form.answers.newStudents || [],
      studentsToRemove: form.answers.studentsToRemove || [],
      familyInfo: form.answers.familyInfo,
      parentGuardians: form.answers.parentGuardians || [],
      emergencyContacts: form.answers.emergencyContacts || [],
      authorizedPickup: form.answers.authorizedPickup || [],
      churchAffiliation: form.answers.churchAffiliation || {},
      studentMedicalInfo: form.answers.studentMedicalInfo,
      medicalConsentSignature: form.answers.medicalConsentSignature,
      tuitionPreferences: form.answers.tuitionPreferences,
      acknowledgments: form.answers.acknowledgments,
      currentStep: form.answers.currentStep,
      availableOptions: form.metadata.availableOptions || {},
      schoolYear: form.metadata.schoolYear,
    };
  }, [form.metadata, form.answers]);

  // Loading
  if (form.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your admissions form...</p>
        </div>
      </div>
    );
  }

  if (replacedBy) return <LinkReplaced schoolName={replacedBy} />;

  // Error
  if (form.error) {
    return (
      <StatusCard
        icon={<AlertCircle className="h-5 w-5" />}
        title="Unable to Load Form"
        titleClassName="text-destructive"
        footer={
          <Button variant="outline" onClick={() => navigate('/app')}>
            Go to Home
          </Button>
        }
      >
        <p className="text-muted-foreground">{form.error}</p>
      </StatusCard>
    );
  }

  // Submitted
  if (submitted) {
    return (
      <StatusCard
        icon={<CheckCircle className="h-5 w-5" />}
        title="Admissions Form Submitted"
        titleClassName="text-green-600"
        footer={
          <Button variant="outline" onClick={() => navigate('/app')}>
            Done
          </Button>
        }
      >
        <p className="text-muted-foreground">
          Thank you for submitting your admissions form for {schoolYearName}!
          The school will review your information and be in touch regarding next steps.
        </p>
      </StatusCard>
    );
  }

  const schoolName = form.metadata?.school?.name || 'Our School';

  return (
    <>
      <WizardFormLayout
        title="Request for Admission"
        subtitle={`${schoolName} - ${schoolYearName}`}
        savingIndicator={{ saving: form.saving, lastSaved: form.lastSaved }}
        steps={form.visibleSteps}
        currentStep={form.currentStep}
        onStepClick={form.goToStep}
        canProceed={form.isLastStep ? form.canProceed : true}
        isFirstStep={form.isFirstStep}
        isLastStep={form.isLastStep}
        onNext={handleNext}
        onPrev={form.prevStep}
        submitLabel="Submit Admissions Form"
        onSubmit={() => setShowSubmitDialog(true)}
        submitting={submitting}
      >
        {/* Step-level validation errors (shown after user clicks Next) */}
        {validationErrors.length > 0 && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 space-y-1">
            {validationErrors.map((error, i) => (
              <div key={i} className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error.message}</span>
              </div>
            ))}
          </div>
        )}

        {form.currentStepId === 'family' && (
          <AppFamilyInfoStep
            formData={formData}
            updateFormData={form.updateAnswers}
            onReviewComplete={handleFamilyInfoReviewed}
            validationErrors={validationErrors}
          />
        )}
        {form.currentStepId === 'students' && (
          <AppStudentsStep
            formData={formData}
            gradeLevels={gradeLevels}
            updateFormData={form.updateAnswers}
          />
        )}
        {form.currentStepId === 'medical' && (
          <AppStudentMedicalStep
            formData={formData}
            updateFormData={form.updateAnswers}
            onReviewComplete={handleMedicalReviewed}
          />
        )}
        {form.currentStepId === 'tuition' && (
          <AppTuitionStep
            formData={formData}
            updateFormData={form.updateAnswers}
          />
        )}
        {form.currentStepId === 'review' && (
          <AppReviewStep
            formData={formData}
            updateFormData={form.updateAnswers}
          />
        )}
      </WizardFormLayout>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Admissions Form?</AlertDialogTitle>
            <AlertDialogDescription>
              Once submitted, you won't be able to make changes. The school will review your
              information and be in touch regarding next steps.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Yes, Submit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default AdmissionsForm;
